import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { Alert, Snackbar } from "@mui/material";

const AuthContext = createContext(null);
const AUTH_BASE_URL = "/api/auth";
const TOKEN_STORAGE_KEY = "edge_token";

function decodeJwtPayload(token) {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch (error) {
    console.error("Failed to decode auth token:", error);
    return null;
  }
}

function getTokenLimitMessage(error) {
  const detail = error.response?.data?.detail;
  const message = typeof detail === "string" ? detail : detail?.message;
  if (error.response?.status !== 403 || !message || !message.toLowerCase().includes("token limit")) {
    return "";
  }
  return "Token limit exceeded. Please contact your admin to increase the limit.";
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_STORAGE_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotaNotice, setQuotaNotice] = useState({ open: false, message: "" });

  const tokenRef = useRef(token);
  const logoutTimerRef = useRef(null);
  const sessionCheckTimerRef = useRef(null);
  const lowTokenNotifiedRef = useRef(false);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const clearAuth = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    tokenRef.current = null;
    setToken(null);
    setUser(null);
    lowTokenNotifiedRef.current = false;
  }, []);

  const scheduleAutoLogout = useCallback((currentToken) => {
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (!currentToken) return;

    const payload = decodeJwtPayload(currentToken);
    const expiresAtMs = payload?.exp ? payload.exp * 1000 : null;

    if (!expiresAtMs) {
      clearAuth();
      return;
    }

    const remainingMs = expiresAtMs - Date.now();
    if (remainingMs <= 0) {
      clearAuth();
      return;
    }

    logoutTimerRef.current = window.setTimeout(() => {
      clearAuth();
    }, remainingMs);
  }, [clearAuth]);

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: "/api" });

    instance.interceptors.request.use((config) => {
      const t = tokenRef.current;
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          clearAuth();
        }
        const tokenLimitMessage = getTokenLimitMessage(error);
        if (tokenLimitMessage) {
          setQuotaNotice({ open: true, message: tokenLimitMessage });
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [clearAuth]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          clearAuth();
        }
        const tokenLimitMessage = getTokenLimitMessage(error);
        if (tokenLimitMessage) {
          setQuotaNotice({ open: true, message: tokenLimitMessage });
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, [clearAuth]);

  useEffect(() => {
    const handleQuotaNotice = (event) => {
      setQuotaNotice({
        open: true,
        message: event.detail?.message || "Token limit exceeded. Please contact your admin to increase the limit.",
      });
    };

    window.addEventListener("quota-limit-exceeded", handleQuotaNotice);
    return () => window.removeEventListener("quota-limit-exceeded", handleQuotaNotice);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (user) {
      const remaining = user.remaining_total_tokens;
      if (remaining !== undefined && remaining < 20000) {
        if (!lowTokenNotifiedRef.current) {
          if (typeof window !== "undefined" && "Notification" in window) {
            if (Notification.permission === "granted") {
              new Notification("Low Token Balance Warning", {
                body: `Your remaining tokens are low (${remaining.toLocaleString()} left). Please contact your administrator.`,
                icon: "/favicon.ico",
              });
              lowTokenNotifiedRef.current = true;
            } else if (Notification.permission === "default") {
              Notification.requestPermission().then((permission) => {
                if (permission === "granted") {
                  new Notification("Low Token Balance Warning", {
                    body: `Your remaining tokens are low (${remaining.toLocaleString()} left). Please contact your administrator.`,
                    icon: "/favicon.ico",
                  });
                  lowTokenNotifiedRef.current = true;
                }
              });
            }
          }
        }
        
        setQuotaNotice((prev) => {
          const msg = `Critically low token balance: Only ${remaining.toLocaleString()} tokens remaining! Please contact an admin to increase your limit.`;
          if (!prev.open || prev.message !== msg) {
            return {
              open: true,
              message: msg,
            };
          }
          return prev;
        });
      } else {
        lowTokenNotifiedRef.current = false;
      }
    } else {
      lowTokenNotifiedRef.current = false;
    }
  }, [user]);

  const fetchUser = useCallback(async (currentToken) => {
    try {
      const res = await axios.get(`${AUTH_BASE_URL}/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setUser(res.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    await fetchUser(currentToken);
  }, [fetchUser]);

  useEffect(() => {
    scheduleAutoLogout(token);

    return () => {
      if (logoutTimerRef.current) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
  }, [token, scheduleAutoLogout]);

  useEffect(() => {
    if (sessionCheckTimerRef.current) {
      window.clearInterval(sessionCheckTimerRef.current);
      sessionCheckTimerRef.current = null;
    }

    if (!token) return undefined;

    sessionCheckTimerRef.current = window.setInterval(() => {
      const currentToken = tokenRef.current;
      if (currentToken) {
        fetchUser(currentToken);
      }
    }, 15000);

    return () => {
      if (sessionCheckTimerRef.current) {
        window.clearInterval(sessionCheckTimerRef.current);
        sessionCheckTimerRef.current = null;
      }
    };
  }, [token, fetchUser]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== TOKEN_STORAGE_KEY) return;
      setToken(event.newValue);
      if (!event.newValue) {
        setUser(null);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchUser(token);
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password, options = {}) => {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    const loginPath = options.force ? "login/force" : "login";
    const res = await axios.post(`${AUTH_BASE_URL}/${loginPath}`, formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });
    
    const newToken = res.data.access_token;
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    tokenRef.current = newToken;
    setToken(newToken);
    await fetchUser(newToken);
  }, [fetchUser]);

  const ssoLogin = useCallback(async (code) => {
    const res = await axios.get(`/api/oauth2/token`, { 
      params: { code } 
    });
    
    // We now receive a native Access Token directly from our Python backend bridged!
    const newToken = res.data.access_token;
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    tokenRef.current = newToken;
    setToken(newToken);
    await fetchUser(newToken);
  }, [fetchUser]);

  const logout = useCallback(() => {
    const currentToken = tokenRef.current;
    if (currentToken) {
      axios.post(`${AUTH_BASE_URL}/logout`, null, {
        headers: { Authorization: `Bearer ${currentToken}` }
      }).catch(() => {});
    }
    clearAuth();
  }, [clearAuth]);

  const checkTokenLimitAndNotify = useCallback(() => {
    if (!user) return false;
    const exceeded = (
      (user.remaining_input_tokens !== undefined && user.remaining_input_tokens <= 0) ||
      (user.remaining_output_tokens !== undefined && user.remaining_output_tokens <= 0) ||
      (user.remaining_total_tokens !== undefined && user.remaining_total_tokens <= 0)
    );
    if (exceeded) {
      window.dispatchEvent(new CustomEvent("quota-limit-exceeded", {
        detail: { message: "Token limit exceeded. Please contact your admin to increase the limit." },
      }));
      return true;
    }
    return false;
  }, [user]);

  const value = useMemo(
    () => ({ token, user, loading, login, ssoLogin, logout, api, refreshUser, checkTokenLimitAndNotify }),
    [token, user, loading, login, ssoLogin, logout, api, refreshUser, checkTokenLimitAndNotify]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <Snackbar
        open={quotaNotice.open}
        autoHideDuration={7000}
        onClose={() => setQuotaNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setQuotaNotice((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {quotaNotice.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { AuthContext as __TEMP_DEV_PREVIEW_AuthContext };
